"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { ArrowLeft, Search, Upload, Loader2, Play, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface InvNRVAssessmentProps {
  onBack?: () => void;
  /** When provided, saved with review comments (workspace name) */
  workspaceName?: string;
  /** When provided, saved with review comments (procedure name) */
  procedureName?: string;
}

export default function InvNRVAssessment({ onBack, workspaceName, procedureName }: InvNRVAssessmentProps) {
  const [fileSearchStatusIR, setFileSearchStatusIR] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [fileSearchStatusSSR, setFileSearchStatusSSR] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundIRFiles, setFoundIRFiles] = useState<string[]>([]);
  const [foundSSRFiles, setFoundSSRFiles] = useState<string[]>([]);
  const [selectedIRFile, setSelectedIRFile] = useState<string>("");
  const [selectedSSRFile, setSelectedSSRFile] = useState<string>("");
  const [referenceIR, setReferenceIR] = useState<string>("");
  const [referenceSSR, setReferenceSSR] = useState<string>("");
  const [uploadIRStatus, setUploadIRStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [uploadSSRStatus, setUploadSSRStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INVENTORY_NRV_ASSESSMENT_${normalizedSection}`;
  };

  const searchForIRFiles = async () => {
    setFileSearchStatusIR("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_INV_IR");
        if (result.success && result.data?.files) {
          const files = result.data.files.map((f: any) => f.name).filter((n: string) => n && n.trim().length > 0);
          if (files.length > 0) { setFoundIRFiles(files); setFileSearchStatusIR("found"); } else { setFileSearchStatusIR("not_found"); }
        } else { setFileSearchStatusIR("not_found"); }
      } else { setFileSearchStatusIR("not_found"); }
    } catch { setFileSearchStatusIR("not_found"); }
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Inventory", "NRV Assessment");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_INV_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_INV_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "NRV-ASSESSMENT-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger both searches on mount
  useEffect(() => {
    searchForIRFiles();
    searchForSSRFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForSSRFiles = async () => {
    setFileSearchStatusSSR("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_INV_SSR");
        if (result.success && result.data?.files) {
          const files = result.data.files.map((f: any) => f.name).filter((n: string) => n && n.trim().length > 0);
          if (files.length > 0) { setFoundSSRFiles(files); setFileSearchStatusSSR("found"); } else { setFileSearchStatusSSR("not_found"); }
        } else { setFileSearchStatusSSR("not_found"); }
      } else { setFileSearchStatusSSR("not_found"); }
    } catch { setFileSearchStatusSSR("not_found"); }
  };

  const uploadIRFile = async () => {
    setUploadIRStatus("uploading");
    try {
      const input = document.createElement('input'); input.type = 'file'; input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve((r.result as string).split(',')[1]); r.onerror = () => reject(new Error('Failed to read')); r.readAsDataURL(file); });
            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({ fileContent: base64Content, fileName: file.name, referenceValue: referenceIR, folderName: "client", fyYear: getFY_YEAR(), sectionList: ["Execution_INV_IR"] });
              if (result.success) { setSelectedIRFile(file.name); setFoundIRFiles([file.name]); setFileSearchStatusIR("found"); setUploadIRStatus("completed"); } else setUploadIRStatus("error");
            } else setUploadIRStatus("error");
          } catch { setUploadIRStatus("error"); }
        }
      };
      input.click();
    } catch { setUploadIRStatus("error"); }
  };

  const uploadSSRFile = async () => {
    setUploadSSRStatus("uploading");
    try {
      const input = document.createElement('input'); input.type = 'file'; input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve((r.result as string).split(',')[1]); r.onerror = () => reject(new Error('Failed to read')); r.readAsDataURL(file); });
            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({ fileContent: base64Content, fileName: file.name, referenceValue: referenceSSR, folderName: "client", fyYear: getFY_YEAR(), sectionList: ["Execution_INV_SSR"] });
              if (result.success) { setSelectedSSRFile(file.name); setFoundSSRFiles([file.name]); setFileSearchStatusSSR("found"); setUploadSSRStatus("completed"); } else setUploadSSRStatus("error");
            } else setUploadSSRStatus("error");
          } catch { setUploadSSRStatus("error"); }
        }
      };
      input.click();
    } catch { setUploadSSRStatus("error"); }
  };

  const canRun = () => selectedIRFile && selectedSSRFile;

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "INV";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "NRV-ASSESSMENT-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  const runNRV = async () => {
    if (!canRun()) return;
    
    // Save file names to file usage reference
    const entries: FileUsageEntry[] = [];
    const files: string[] = [];
    if (selectedIRFile) {
      const fileName = selectedIRFile.includes(' (') ? selectedIRFile.split(' (')[0] : selectedIRFile;
      files.push(fileName);
    }
    if (selectedSSRFile) {
      const fileName = selectedSSRFile.includes(' (') ? selectedSSRFile.split(' (')[0] : selectedSSRFile;
      files.push(fileName);
    }
    if (files.length > 0) {
      entries.push({ "Files in NRV Assessment": files.join(', ') });
      await updateProcedureFileUsageForWorkspace("Inventory", "NRV Assessment", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_inv_nrv_assessment", { inputFiles: [], options: {} });
        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else setProcessingStatus("error");
    } catch { setProcessingStatus("error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">NRV Assessment - Inventory</h2>
          <p className="text-gray-400">Select Inventory Register and Subsequent Sales Register, then run NRV Assessment</p>
        </div>
        {onBack && (<Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>)}
      </div>

      {/* Inventory Register */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Search className="h-5 w-5 text-blue-500" />Step 1: Search for Inventory Register</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Inventory Register"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatusIR === "found" && (<span className="text-green-400 font-bold"> FOUND!</span>)}
            {fileSearchStatusIR === "not_found" && (<span className="text-yellow-400">No files found</span>)}
          </div>
          {fileSearchStatusIR === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Inventory Register</Label>
                <Select value={selectedIRFile} onValueChange={setSelectedIRFile}>
                  <SelectTrigger><SelectValue placeholder="Select file" /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{foundIRFiles.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Files in NRV Assessment"]) && (() => {
                  const files = fileUsageReference.find(e => e["Files in NRV Assessment"])?.["Files in NRV Assessment"] || "";
                  const fileList = files.split(', ').filter(f => f);
                  const irFile = fileList[0] || "";
                  return irFile ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {irFile}
                    </p>
                  ) : null;
                })()}
              </div>
            </div>
          )}
          {fileSearchStatusIR === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2"><Label className="text-white">Reference Value (optional)</Label><Input value={referenceIR} onChange={(e) => setReferenceIR(e.target.value)} placeholder="Enter reference value" className="border-white/10 bg-black/40 text-white" /></div>
                <Button onClick={uploadIRFile} disabled={uploadIRStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">{uploadIRStatus === "uploading" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="h-4 w-4 mr-2" /> Upload File</>)}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subsequent Sales Register */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Search className="h-5 w-5 text-blue-500" />Step 2: Search for Subsequent Sales Register</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Search for Subsequent Sales Register"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatusSSR === "found" && (<span className="text-green-400 font-bold"> FOUND!</span>)}
            {fileSearchStatusSSR === "not_found" && (<span className="text-yellow-400">No files found</span>)}
          </div>
          {fileSearchStatusSSR === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Subsequent Sales Register</Label>
                <Select value={selectedSSRFile} onValueChange={setSelectedSSRFile}>
                  <SelectTrigger><SelectValue placeholder="Select file" /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{foundSSRFiles.map((f) => (<SelectItem key={f} value={f}>{f}</SelectItem>))}                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Files in NRV Assessment"]) && (() => {
                  const files = fileUsageReference.find(e => e["Files in NRV Assessment"])?.["Files in NRV Assessment"] || "";
                  const fileList = files.split(', ').filter(f => f);
                  const ssrFile = fileList[1] || "";
                  return ssrFile ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {ssrFile}
                    </p>
                  ) : null;
                })()}
              </div>
            </div>
          )}
          {fileSearchStatusSSR === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2"><Label className="text-white">Reference Value (optional)</Label><Input value={referenceSSR} onChange={(e) => setReferenceSSR(e.target.value)} placeholder="Enter reference value" className="border-white/10 bg-black/40 text-white" /></div>
                <Button onClick={uploadSSRFile} disabled={uploadSSRStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">{uploadSSRStatus === "uploading" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="h-4 w-4 mr-2" /> Upload File</>)}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run NRV Assessment */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Play className="h-5 w-5 text-green-500" />Run NRV Assessment</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run NRV Assessment"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={runNRV} disabled={!canRun() || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>) : (<>Run NRV Assessment</>)}
            </Button>
            {processingStatus === "completed" && (<span className="text-green-400">Successful</span>)}
            {processingStatus === "error" && (<span className="text-red-400">Failed</span>)}
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="inventory"
            itemId="NRV-ASSESSMENT-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Inventory Register")]: "Step 1: Search for Inventory Register",
              [getSectionQuestionId("Step 2: Search for Subsequent Sales Register")]: "Step 2: Search for Subsequent Sales Register",
              [getSectionQuestionId("Run NRV Assessment")]: "Run NRV Assessment",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={workspaceName}
          subPhase={procedureName}
        />
      )}
    </div>
  );
}


